import { ApiRef, createApiRef } from '@backstage/frontend-plugin-api';
import type {
  EnumEnvironmentOperationType,
  RequestEnvironment,
  RequestEnvironmentConfig,
  RequestScaleUp,
  ResponseDiffReport,
  ResponseEnvironment,
  ResponseEnvironmentConfig,
  ResponseEnvironmentConfigList,
  ResponseEnvironmentCreated,
  ResponseEnvironmentList,
  ResponseOpCreated,
  ResponseRegionList,
  V3EnvironmentsEnvironmentIDClustersPostRequest,
  V3EnvironmentsEnvironmentIDComponentsPostRequest,
  V3EnvironmentsEnvironmentIDOwnersPutRequest,
} from '@internal/backstage-plugin-neo4j-environment-manager-common';
import {
  EnumOwnerReferenceKind,
  AuratypesCloudProvider,
  V1ClusterRole,
} from '@internal/backstage-plugin-neo4j-environment-manager-common';

export enum ClusterState {
  DEPLOYED,
  CREATING,
  UNREQUESTED,
}

export const AuratypesTenancy = {
  TenancySingle: 'single',
  TenancyMultiple: 'multiple',
  TenancyShared: 'shared',
  TenancyPoc: 'poc',
  TenancyNone: 'none',
} as const;
export type AuratypesTenancy =
  (typeof AuratypesTenancy)[keyof typeof AuratypesTenancy];

// Re-export SDK enums for convenience
export { EnumOwnerReferenceKind, AuratypesCloudProvider, V1ClusterRole };

export type DispatchOption = 'NONE' | 'FULL' | 'DRY_RUN';

// Domain Types

export type Owner = {
  identifier: string;
  kind: EnumOwnerReferenceKind;
};

export type Regions = Record<AuratypesCloudProvider, Set<string>>;
export type CloudProviderIds = Record<AuratypesCloudProvider, Set<string>>;

export type RegionMismatch = {
  clusterIndex: number;
  region: string;
  cloudProvider: string;
};

export type Cluster = {
  name?: string;
  cloudProvider: AuratypesCloudProvider;
  /**
   * A cluster can serve several roles at once, e.g. `[orchestra, object-storage]`.
   * The API names clusters by role membership (brain, then orchestra, then any
   * other). Empty only while a newly added cluster is waiting for its first role.
   */
  roles: V1ClusterRole[];
  region: string;
  gcpProjectId?: string;
  awsAccountId?: string;
  azureSubscriptionId?: string;
  state: ClusterState;
  overrideDefaultCloudProviderHost: boolean;
  overrideDefaultManagementGcpProject: boolean;
  tenancy: AuratypesTenancy;
  /** Groups clusters into a shared partition. Derived by the API when unset. */
  partitionName?: string;
  /**
   * Form-only marker: the user has changed this cluster's provider or region,
   * so a later role must not overwrite the choice with its own location
   * default. `buildClusterSpec` builds the request field by field, so this
   * never reaches the API.
   */
  locationTouched?: boolean;
};

export type ConfigureEnvironmentFormData = {
  name: string;
  initialOwners: Owner[];
  newComponents: string[];
  newClusters: Cluster[];
};

// Default constants

export const DEFAULT_CLOUD_PROVIDER_HOST = 'default';
export const DEFAULT_GCP_REGION = 'europe-west1';
export const DEFAULT_AWS_REGION = 'eu-west-1';
export const DEFAULT_AZURE_REGION = 'uksouth';

const DEFAULT_REGION_BY_PROVIDER: Record<AuratypesCloudProvider, string> = {
  [AuratypesCloudProvider.CloudProviderGCP]: DEFAULT_GCP_REGION,
  [AuratypesCloudProvider.CloudProviderAWS]: DEFAULT_AWS_REGION,
  [AuratypesCloudProvider.CloudProviderAzure]: DEFAULT_AZURE_REGION,
};

/** Brain's cloud provider is enforced server-side, so the form only reports it. */
export const BRAIN_PROVIDER_NOTE = 'Brain always runs on GCP.';

type CloudProviderDefaults = Pick<
  Cluster,
  | 'region'
  | 'gcpProjectId'
  | 'awsAccountId'
  | 'azureSubscriptionId'
  | 'overrideDefaultCloudProviderHost'
  | 'overrideDefaultManagementGcpProject'
>;

/**
 * The region and isolation-unit fields a cluster gets when it moves to
 * `cloudProvider`. Accounts, projects and their override switches are scoped to
 * the provider that was picked when they were entered, so a provider change
 * makes them stale wherever it comes from — the dropdown or an adopted role.
 */
export const cloudProviderDefaults = (
  cloudProvider: AuratypesCloudProvider,
): CloudProviderDefaults => ({
  region: DEFAULT_REGION_BY_PROVIDER[cloudProvider],
  gcpProjectId: DEFAULT_CLOUD_PROVIDER_HOST,
  awsAccountId: DEFAULT_CLOUD_PROVIDER_HOST,
  azureSubscriptionId: DEFAULT_CLOUD_PROVIDER_HOST,
  overrideDefaultCloudProviderHost: false,
  overrideDefaultManagementGcpProject: false,
});

export const EDIT_ENVIRONMENT_DISABLED_MESSAGE =
  'Cluster cannot be updated whilst other modifications are ongoing';

export const EDIT_EXISTING_CLUSTER_DISABLED_MESSAGE =
  'Cannot edit existing cluster';

export const PLATFORMX_ENVIRONMENTS_EVENT_ID = 'aura.portal.environments';

const DEFAULT_GCP_CLUSTER_BASE = {
  cloudProvider: AuratypesCloudProvider.CloudProviderGCP,
  region: DEFAULT_GCP_REGION,
  gcpProjectId: DEFAULT_CLOUD_PROVIDER_HOST,
  awsAccountId: DEFAULT_CLOUD_PROVIDER_HOST,
  azureSubscriptionId: DEFAULT_CLOUD_PROVIDER_HOST,
  state: ClusterState.UNREQUESTED,
  overrideDefaultCloudProviderHost: false,
  overrideDefaultManagementGcpProject: false,
  tenancy: AuratypesTenancy.TenancyNone,
} satisfies Partial<Cluster>;

const DEFAULT_AWS_CLUSTER_BASE = {
  ...DEFAULT_GCP_CLUSTER_BASE,
  cloudProvider: AuratypesCloudProvider.CloudProviderAWS,
  region: DEFAULT_AWS_REGION,
} satisfies Partial<Cluster>;

/** A freshly added cluster, waiting for the user to pick its first role. */
export const DEFAULT_CLUSTER: Cluster = {
  ...DEFAULT_GCP_CLUSTER_BASE,
  roles: [],
};

export const DEFAULT_BRAIN: Cluster = {
  ...DEFAULT_GCP_CLUSTER_BASE,
  roles: [V1ClusterRole.ClusterRoleBrain],
  tenancy: AuratypesTenancy.TenancyMultiple,
};

export const DEFAULT_BRAIN_CSP: Cluster = {
  ...DEFAULT_AWS_CLUSTER_BASE,
  roles: [V1ClusterRole.ClusterRoleCspBrain],
  tenancy: AuratypesTenancy.TenancyMultiple,
};

export const DEFAULT_ORCHESTRA: Cluster = {
  ...DEFAULT_GCP_CLUSTER_BASE,
  roles: [V1ClusterRole.ClusterRoleOrchestra],
  tenancy: AuratypesTenancy.TenancyMultiple,
};

export const DEFAULT_INGRESS: Cluster = {
  ...DEFAULT_AWS_CLUSTER_BASE,
  roles: [V1ClusterRole.ClusterRoleIngress],
};

export const DEFAULT_APPLICATION: Cluster = {
  ...DEFAULT_AWS_CLUSTER_BASE,
  roles: [V1ClusterRole.ClusterRoleApplication],
};

export const DEFAULT_OMNI_CD: Cluster = {
  ...DEFAULT_AWS_CLUSTER_BASE,
  roles: [V1ClusterRole.ClusterRoleOmniCd],
};

export const DEFAULT_OMNI_CI: Cluster = {
  ...DEFAULT_AWS_CLUSTER_BASE,
  roles: [V1ClusterRole.ClusterRoleOmniCi],
};

export const DEFAULT_OMNI_SURFACE: Cluster = {
  ...DEFAULT_AWS_CLUSTER_BASE,
  roles: [V1ClusterRole.ClusterRoleOmniSurface],
};

export const DEFAULT_OBJECT_STORAGE: Cluster = {
  ...DEFAULT_GCP_CLUSTER_BASE,
  roles: [V1ClusterRole.ClusterRoleObjectStorage],
};

export const DEFAULT_OMNI_IMS_MANAGEMENT: Cluster = {
  ...DEFAULT_GCP_CLUSTER_BASE,
  roles: [V1ClusterRole.ClusterRoleOmniImsManagement],
};

export const DEFAULT_OMNI_IMS_PROVISIONING: Cluster = {
  ...DEFAULT_GCP_CLUSTER_BASE,
  roles: [V1ClusterRole.ClusterRoleOmniImsProvisioning],
};

/**
 * Provider, region and tenancy defaults applied when a cluster's first role is
 * picked, so e.g. choosing Ingress lands on AWS rather than the neutral GCP
 * defaults a role-less cluster starts with.
 */
export const DEFAULT_CLUSTER_BY_ROLE: Record<V1ClusterRole, Cluster> = {
  [V1ClusterRole.ClusterRoleBrain]: DEFAULT_BRAIN,
  [V1ClusterRole.ClusterRoleOrchestra]: DEFAULT_ORCHESTRA,
  [V1ClusterRole.ClusterRoleIngress]: DEFAULT_INGRESS,
  [V1ClusterRole.ClusterRoleApplication]: DEFAULT_APPLICATION,
  [V1ClusterRole.ClusterRoleObjectStorage]: DEFAULT_OBJECT_STORAGE,
  [V1ClusterRole.ClusterRoleCspBrain]: DEFAULT_BRAIN_CSP,
  [V1ClusterRole.ClusterRoleOmniCd]: DEFAULT_OMNI_CD,
  [V1ClusterRole.ClusterRoleOmniCi]: DEFAULT_OMNI_CI,
  [V1ClusterRole.ClusterRoleOmniSurface]: DEFAULT_OMNI_SURFACE,
  [V1ClusterRole.ClusterRoleOmniImsManagement]: DEFAULT_OMNI_IMS_MANAGEMENT,
  [V1ClusterRole.ClusterRoleOmniImsProvisioning]: DEFAULT_OMNI_IMS_PROVISIONING,
};

// Bypasses the SDK's whitelist-based ToJSON serialization, which drops unknown
// fields. Used to preserve raw cluster definitions during environment updates.
export type RawEnvironmentBody = {
  metadata: { name: string };
  spec: {
    clusters: Record<string, unknown>[];
    components: { name: string }[];
    owner_identifiers: { identifier: string; kind: string }[];
  };
};

export interface OmniEnvironmentsApi {
  getEnvironment(
    environmentID: string,
    includeStatusHistory?: boolean,
  ): Promise<ResponseEnvironment>;
  getEnvironmentRawJson(environmentID: string): Promise<unknown>;
  getEnvironmentWithRawClusters(environmentID: string): Promise<{
    environment: ResponseEnvironment;
    rawClusterDefinitions: Record<string, unknown>[];
  }>;
  getEnvironments(options?: {
    searchTerm?: string;
    includeDeleted?: boolean;
    ownerIdentifier?: string;
  }): Promise<ResponseEnvironmentList>;
  getEnvironmentNames(): Promise<string[]>;
  createEnvironment(request: {
    environment: RequestEnvironment;
    dispatchOption?: DispatchOption;
  }): Promise<ResponseEnvironmentCreated>;
  deleteEnvironment(request: {
    environmentID: string;
    dispatchOption?: DispatchOption;
  }): Promise<ResponseOpCreated | null | undefined>;
  updateEnvironment(request: {
    environmentID: string;
    dispatchOption?: DispatchOption;
  }): Promise<ResponseOpCreated | null | undefined>;
  addClusters(
    request: V3EnvironmentsEnvironmentIDClustersPostRequest,
  ): Promise<ResponseOpCreated | null | undefined>;
  addComponents(
    request: V3EnvironmentsEnvironmentIDComponentsPostRequest,
  ): Promise<ResponseOpCreated | null | undefined>;
  scaleUpEnvironment(request: {
    environmentID: string;
    duration: RequestScaleUp;
  }): Promise<ResponseOpCreated | null | undefined>;
  scaleDownEnvironment(request: {
    environmentID: string;
  }): Promise<ResponseOpCreated | null | undefined>;
  cancelOperation(request: {
    environmentID: string;
    operationID: number;
    name: string;
    type: EnumEnvironmentOperationType;
  }): Promise<void>;
  replaceOwners(
    request: V3EnvironmentsEnvironmentIDOwnersPutRequest,
  ): Promise<void>;
  putEnvironmentUntyped(
    environmentID: string,
    body: RawEnvironmentBody,
  ): Promise<ResponseDiffReport | null | undefined>;
  getEnvironmentConfig(
    environmentID: string,
    options?: { stripIdentifiers?: boolean },
  ): Promise<ResponseEnvironmentConfig>;
  getRegions(): Promise<ResponseRegionList>;
}

export const environmentsApiRef: ApiRef<OmniEnvironmentsApi> =
  createApiRef<OmniEnvironmentsApi>({
    id: 'plugin.neo4j-environment-manager.service',
  });

// Configs API

export interface ConfigsRawJsonCache {
  items: Array<{
    name: string;
    config: any;
  }>;
}

export interface GetConfigsResult {
  configs: ResponseEnvironmentConfigList;
  rawJson: ConfigsRawJsonCache;
}

export interface ConfigsApi {
  getConfigs(): Promise<GetConfigsResult>;
  deleteConfig(configName: string): Promise<void>;
  createConfig(request: {
    configName: string;
    config: RequestEnvironmentConfig;
  }): Promise<void>;
}

export const configsApiRef: ApiRef<ConfigsApi> = createApiRef<ConfigsApi>({
  id: 'plugin.neo4j-environment-manager.configs',
});
